import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './api.js';

/**
 * Training, on the web.
 *
 * Built around the one question that matters while you are standing in front of
 * the bar: what did I do last time. Everything else on this page is secondary
 * to that, and the layout says so.
 *
 * The other deliberate choice is the exercise-calories setting, which lives
 * here rather than in a settings screen. It is a training decision, it changes
 * what the Today screen says, and burying it somewhere general is how it gets
 * switched on without anybody understanding what it does.
 */

interface Exercise {
  id: string; name: string; muscle: string; category: string;
  isCompound: boolean; tracks1rm: boolean; isMine: boolean;
}

interface TrainingSet {
  id: string; exerciseId: string; exercise: string; muscle: string; setNo: number;
  weightKg: number | null; reps: number | null; rpe: number | null; rir: number | null;
  isWarmup: boolean; toFailure: boolean; durationSec: number | null; distanceM: number | null;
  estimated1rm?: number | null; describe?: string;
}

interface Session {
  id: string; name: string | null; type: string; performedAt: string; finishedAt: string | null;
  durationMin: number | null; bodyweightKg: number | null;
  sets: TrainingSet[]; volumeKg: number; workingSetCount: number;
  byMuscle?: Array<{ muscle: string; sets: number; volumeKg: number }>;
}

interface Last {
  last: { performedAt: string; sets: TrainingSet[] } | null;
  trend: Array<{ date: string; estimated1rm: number | null }>;
}

interface Settings { addsToBudget: boolean; creditPct: number; explanation: string }

const kg = (v: number | null): string => (v == null ? '—' : `${v} kg`);

function describeSet(s: TrainingSet): string {
  if (s.reps != null && s.weightKg != null) return `${s.weightKg} kg × ${s.reps}`;
  if (s.reps != null) return `${s.reps} reps`;
  if (s.distanceM != null) return `${(s.distanceM / 1000).toFixed(2)} km`;
  if (s.durationSec != null) return `${Math.round(s.durationSec / 60)} min`;
  return '—';
}

export function Training(): JSX.Element {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Exercise | null>(null);
  const [last, setLast] = useState<Last | null>(null);
  const [form, setForm] = useState({ weightKg: '', reps: '', rpe: '' });
  const [settings, setSettings] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSessions = useCallback(async () => {
    try {
      const r = await api<{ sessions: Session[]; openSessionId: string | null }>('/training/sessions');
      setSessions(r.sessions);
      setOpenId(r.openSessionId);
      if (r.openSessionId) setSession(await api<Session>(`/training/sessions/${r.openSessionId}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load training.');
    }
  }, []);

  useEffect(() => {
    void loadSessions();
    void api<Settings>('/training/settings').then(setSettings).catch(() => undefined);
  }, [loadSessions]);

  useEffect(() => {
    const t = setTimeout(() => {
      void api<{ exercises: Exercise[] }>(`/training/exercises?q=${encodeURIComponent(q)}`)
        .then((r) => setExercises(r.exercises))
        .catch(() => undefined);
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const pick = async (e: Exercise): Promise<void> => {
    setPicked(e);
    setLast(null);
    try {
      setLast(await api<Last>(`/training/exercises/${e.id}/last`));
    } catch { /* a first-ever exercise has no last, which is not an error */ }
  };

  const start = async (): Promise<void> => {
    try {
      const s = await api<Session>('/training/sessions', {
        method: 'POST', body: JSON.stringify({ type: 'resistance' }),
      });
      setSession(s);
      setOpenId(s.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start a session.');
    }
  };

  const addSet = async (): Promise<void> => {
    if (!session || !picked) return;
    const body: Record<string, unknown> = {
      exerciseId: picked.id,
      weightKg: form.weightKg === '' ? null : Number(form.weightKg),
      reps: form.reps === '' ? null : Number(form.reps),
      rpe: form.rpe === '' ? null : Number(form.rpe),
    };
    try {
      setSession(await api<Session>(`/training/sessions/${session.id}/sets`, {
        method: 'POST', body: JSON.stringify(body),
      }));
      // Weight is kept and reps are kept: the next set is usually the same
      // again. Clearing them would mean retyping the identical numbers five
      // times a session.
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That set did not save.');
    }
  };

  const removeSet = async (id: string): Promise<void> => {
    if (!session) return;
    await api<void>(`/training/sets/${id}`, { method: 'DELETE' }).catch(() => undefined);
    setSession(await api<Session>(`/training/sessions/${session.id}`));
  };

  const finish = async (): Promise<void> => {
    if (!session) return;
    await api<unknown>(`/training/sessions/${session.id}/finish`, { method: 'POST', body: '{}' })
      .catch(() => undefined);
    setSession(null);
    setOpenId(null);
    void loadSessions();
  };

  const saveSettings = async (next: Settings): Promise<void> => {
    setSettings(next);
    await api<unknown>('/training/settings', {
      method: 'PUT',
      body: JSON.stringify({ addsToBudget: next.addsToBudget, creditPct: next.creditPct }),
    }).catch(() => undefined);
  };

  const byExercise = useMemo(() => {
    const map = new Map<string, TrainingSet[]>();
    for (const s of session?.sets ?? []) {
      const list = map.get(s.exercise) ?? [];
      list.push(s);
      map.set(s.exercise, list);
    }
    return [...map.entries()];
  }, [session]);

  return (
    <>
      {!session && (
        <div className="card">
          <h2>No session open</h2>
          <button type="button" onClick={() => void start()}>Start a session</button>
        </div>
      )}

      {session && (
        <div className="card">
          <h2>{session.name ?? 'Session'}</h2>
          <p className="muted small">
            {session.workingSetCount} working set{session.workingSetCount === 1 ? '' : 's'},
            {' '}{Math.round(session.volumeKg).toLocaleString()} kg of volume.
            {' '}Warm-ups are excluded from both, because counting them makes every week look
            productive.
          </p>

          {byExercise.map(([name, sets]) => (
            <div key={name} className="stack">
              <strong>{name}</strong>
              {sets.map((s) => (
                <div key={s.id} className="row between">
                  <span>
                    {s.isWarmup ? <span className="muted small">warm-up </span> : null}
                    {describeSet(s)}
                    {s.rpe != null ? <span className="muted small"> @ RPE {s.rpe}</span> : null}
                  </span>
                  <button type="button" className="ghost" onClick={() => void removeSet(s.id)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ))}

          <button type="button" className="ghost" onClick={() => void finish()}>Finish session</button>
        </div>
      )}

      <div className="card">
        <h2>Add a set</h2>
        <input placeholder="Search exercises" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="row wrap">
          {exercises.slice(0, 12).map((e) => (
            <button key={e.id} type="button"
                    className={picked?.id === e.id ? 'on' : ''}
                    onClick={() => void pick(e)}>
              {e.name}
            </button>
          ))}
        </div>

        {picked && (
          <>
            {/* The question you actually have at the bar, answered before the
                inputs rather than below them. */}
            {last?.last ? (
              <p className="muted small">
                Last time ({new Date(last.last.performedAt).toLocaleDateString()}):{' '}
                {last.last.sets.filter((s) => !s.isWarmup).map(describeSet).join(', ') || 'nothing recorded'}
              </p>
            ) : (
              <p className="muted small">Nothing logged for this one yet.</p>
            )}

            <div className="row wrap">
              <label className="field">
                <span>Weight</span>
                <input type="number" step="0.5" value={form.weightKg}
                       onChange={(e) => setForm((f) => ({ ...f, weightKg: e.target.value }))} />
              </label>
              <label className="field">
                <span>Reps</span>
                <input type="number" value={form.reps}
                       onChange={(e) => setForm((f) => ({ ...f, reps: e.target.value }))} />
              </label>
              <label className="field">
                <span>RPE</span>
                <input type="number" step="0.5" min="1" max="10" value={form.rpe}
                       onChange={(e) => setForm((f) => ({ ...f, rpe: e.target.value }))} />
              </label>
            </div>
            <button type="button" disabled={!session} onClick={() => void addSet()}>
              {session ? 'Add set' : 'Start a session first'}
            </button>
          </>
        )}
      </div>

      {settings && (
        <div className="card">
          <h2>Do workouts raise the day's budget?</h2>
          <div className="row wrap">
            <button type="button"
                    className={settings.addsToBudget ? '' : 'on'}
                    onClick={() => void saveSettings({ ...settings, addsToBudget: false })}>
              No
            </button>
            <button type="button"
                    className={settings.addsToBudget ? 'on' : ''}
                    onClick={() => void saveSettings({ ...settings, addsToBudget: true })}>
              Yes, partly
            </button>
          </div>
          {settings.addsToBudget && (
            <label className="field">
              <span>Credit {settings.creditPct}% of the estimate</span>
              <input type="range" min={0} max={100} step={5} value={settings.creditPct}
                     onChange={(e) => void saveSettings({ ...settings, creditPct: Number(e.target.value) })} />
            </label>
          )}
          <p className="muted small">{settings.explanation}</p>
        </div>
      )}

      {sessions.length > 0 && (
        <div className="card">
          <h2>Recent sessions</h2>
          {sessions.slice(0, 10).map((s) => (
            <div key={s.id} className="row between">
              <span>{new Date(s.performedAt).toLocaleDateString()} — {s.name ?? s.type}</span>
              <span className="muted small">
                {s.workingSetCount} sets, {Math.round(s.volumeKg).toLocaleString()} kg
              </span>
            </div>
          ))}
        </div>
      )}

      {openId && !session && <p className="muted small">Reopening your unfinished session…</p>}
      {error && <p className="bad">{error}</p>}
    </>
  );
}
